import * as THREE from "three";
import {
	magnetizeRotationDeg,
	rotationRadiansY,
	snapRotationDeg,
} from "../../domain/viewer/saunaRotation";

/**
 * Turn-the-building gesture.
 *
 * The pointer is projected onto a horizontal plane through the model and its
 * bearing around the pivot (the rig's own origin) is tracked. The rig follows
 * the pointer live — with a magnet near each cardinal so the user can see
 * where the release will land — and the value that is COMMITTED is always
 * snapped. See `domain/viewer/saunaRotation` for why the commit set is only
 * four values.
 *
 * The live preview writes straight to `rig.rotation.y`; it never goes through
 * React state. That keeps a continuous gesture off the render path entirely
 * and — more importantly — keeps it out of the undo history: exactly one
 * commit reaches the store, on release.
 */

interface RotationDragConfig {
	/** The rotated group; its `rotation.y` is written live during the drag. */
	rig: THREE.Object3D;
	camera: THREE.PerspectiveCamera;
	renderer: THREE.WebGLRenderer;
	/** Rotation the gesture starts from, in degrees. */
	rotationDeg: number;
	/** World Y of the horizontal plane the pointer is projected onto. */
	planeY: number;
	/** Fired once, on release, with a snapped cardinal angle. */
	onCommit: (rotationDeg: number) => void;
	onCancel?: () => void;
	/** Live preview angle in degrees (already magnetized). */
	onDragUpdate?: (previewDeg: number) => void;
}

/**
 * Bearing of a world point around the vertical axis through the origin,
 * measured from +Z towards +X — the same sense as `rotation.y`, so a bearing
 * delta IS a rotation delta.
 */
export function bearingDeg(x: number, z: number): number {
	return (Math.atan2(x, z) * 180) / Math.PI;
}

export class RotationDragController {
	private rig: THREE.Object3D;
	private camera: THREE.PerspectiveCamera;
	private domElement: HTMLElement;
	private onCommit: (rotationDeg: number) => void;
	private onCancel?: () => void;
	private onDragUpdate?: (previewDeg: number) => void;

	private startDeg: number;
	private currentDeg: number;
	private grabBearingDeg: number | null = null;
	private movePlane: THREE.Plane;
	private raycaster = new THREE.Raycaster();
	private pointer = new THREE.Vector2();
	private hit = new THREE.Vector3();
	private active = false;

	private boundPointerMove: (e: PointerEvent) => void;
	private boundPointerUp: (e: PointerEvent) => void;
	private boundKeyDown: (e: KeyboardEvent) => void;

	constructor(config: RotationDragConfig) {
		this.rig = config.rig;
		this.camera = config.camera;
		this.domElement = config.renderer.domElement;
		this.onCommit = config.onCommit;
		this.onCancel = config.onCancel;
		this.onDragUpdate = config.onDragUpdate;
		this.startDeg = config.rotationDeg;
		this.currentDeg = config.rotationDeg;
		this.movePlane = new THREE.Plane(
			new THREE.Vector3(0, 1, 0),
			-config.planeY,
		);

		this.boundPointerMove = this.handlePointerMove.bind(this);
		this.boundPointerUp = this.handlePointerUp.bind(this);
		this.boundKeyDown = this.handleKeyDown.bind(this);
	}

	startDrag(): void {
		this.active = true;
		this.domElement.addEventListener("pointermove", this.boundPointerMove);
		this.domElement.addEventListener("pointerup", this.boundPointerUp);
		this.domElement.addEventListener("pointercancel", this.boundPointerUp);
		window.addEventListener("keydown", this.boundKeyDown);
		this.domElement.style.cursor = "grabbing";
	}

	cancelDrag(): void {
		// Put the building back exactly where the gesture found it.
		this.applyPreview(this.startDeg);
		this.cleanup();
		this.onCancel?.();
	}

	dispose(): void {
		this.cleanup();
	}

	private projectPointer(event: PointerEvent): number | null {
		const rect = this.domElement.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0) return null;
		this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
		this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
		this.raycaster.setFromCamera(this.pointer, this.camera);
		if (!this.raycaster.ray.intersectPlane(this.movePlane, this.hit)) {
			return null;
		}
		// Straight overhead the bearing is undefined and jitters wildly; a
		// dead zone around the pivot is better than a spin.
		if (Math.hypot(this.hit.x, this.hit.z) < 0.05) return null;
		return bearingDeg(this.hit.x, this.hit.z);
	}

	private handlePointerMove(event: PointerEvent): void {
		if (!this.active) return;
		const bearing = this.projectPointer(event);
		if (bearing === null) return;

		if (this.grabBearingDeg === null) {
			// First move sets the reference, so grabbing the handle never
			// teleports the building to the pointer.
			this.grabBearingDeg = bearing;
			return;
		}

		this.applyPreview(
			magnetizeRotationDeg(this.startDeg + (bearing - this.grabBearingDeg)),
		);
	}

	private applyPreview(deg: number): void {
		this.currentDeg = deg;
		this.rig.rotation.y = rotationRadiansY(deg);
		this.onDragUpdate?.(deg);
	}

	private handlePointerUp(): void {
		if (!this.active) return;
		const committed = snapRotationDeg(this.currentDeg);
		// Land on the snapped angle immediately: React will re-render with the
		// same value a tick later, so waiting for it would show one frame of
		// the un-snapped preview.
		this.applyPreview(committed);
		this.cleanup();
		this.onCommit(committed);
	}

	private handleKeyDown(event: KeyboardEvent): void {
		if (event.key === "Escape") {
			this.cancelDrag();
		}
	}

	private cleanup(): void {
		this.active = false;
		this.grabBearingDeg = null;
		this.domElement.removeEventListener("pointermove", this.boundPointerMove);
		this.domElement.removeEventListener("pointerup", this.boundPointerUp);
		this.domElement.removeEventListener("pointercancel", this.boundPointerUp);
		window.removeEventListener("keydown", this.boundKeyDown);
		this.domElement.style.cursor = "";
	}
}
